/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useEffect, useMemo } from 'react';
import { defaultJobsDatableColumns, Job, JobsDataTable, useProcessesContext } from 'ogc-processes-components';
import { NavLink, useNavigate, useParams } from 'react-router';
import { jobCommander, useQueryFilters } from '@/hooks/useQueryFilters.tsx';
import { DataTableFilterMeta } from 'primereact/datatable';
import { Utils } from '@/utils/utils.ts';
import { AppRoutes } from '@/constants/routes.ts';
import { FilterMatchMode } from 'primereact/api';
import { useQuery } from '@tanstack/react-query';
import { ActionBodyRender } from '@/pages/JobsPage/JobProcessHistoryPage/ActionBodyRender';
import { useTranslation } from 'react-i18next';
import { DEFAULT_AUTO_REFRESH_INTERVAL } from '@/constants/constants';
import { useInterval } from 'primereact/hooks';

interface Props {
  setProcessId?: React.Dispatch<React.SetStateAction<string | undefined>>;
}
export const JobProcessHistoryPage: React.FC<Props> = ({ setProcessId }) => {
  const { processesEndpoint } = useProcessesContext();

  const { t } = useTranslation();

  const { '*': processId } = useParams();

  const { prefilters } = useQueryFilters({ commander: jobCommander, filter_prefix: 'job' });

  const navigate = useNavigate();

  const { data, isPending, refetch } = useQuery({
    queryKey: ['get_project_jobs', processId, prefilters],
    queryFn: async () => {
      const query: Record<string, string> = { process_id_list: processId! };
      Object.entries(prefilters).forEach(([k, v]) => {
        if (k !== 'reference') {
          query[k] = v as string;
        }
      });

      return processesEndpoint.get_jobs(query);
    },
    refetchOnWindowFocus: false,
    refetchInterval: DEFAULT_AUTO_REFRESH_INTERVAL
  });

  const getCapabilities = useMemo(() => {
    if (data?.links && data.links[0]) {
      return {
        title: 'Capabilities',
        link: data.links[0].href,
        formatter: processesEndpoint.hrefFormatter
      };
    }
  }, [data, processesEndpoint.hrefFormatter]);

  useInterval(
    () => {
      refetch?.();
    },
    DEFAULT_AUTO_REFRESH_INTERVAL,
    true
  );

  useEffect(() => {
    if (processId) {
      setProcessId?.(processId);
    }
    return () => {
      setProcessId?.(undefined);
    };
  }, [processId, setProcessId]);

  const updateFilters = (filters: DataTableFilterMeta, deferred = false) => {
    const fn = (f: DataTableFilterMeta) => {
      const params = Utils.filtersToQueryParams(f, 'job');
      navigate(AppRoutes.jobs.processHistory.url(processId!) + Utils.buildWithQueryParamsUrl(params));
      refetch();
    };
    if (deferred) {
      setTimeout(() => {
        fn(filters);
      }, 1200);
      return;
    }
    fn(filters);
  };

  const columns = useMemo(() => {
    if (defaultJobsDatableColumns) {
      return [
        ...defaultJobsDatableColumns
          .filter((el) => el.field !== 'processID')
          .map((column) => ({
            ...column,
            header:
              column.field !== 'status' ? t([`jobs.properties.${column.field}`, `${column.header}`]) : column.header
          })),
        {
          order: 0,
          field: 'project_ref',
          header: 'Version ',
          sortable: true,
          filter: true,
          hidden: processId?.startsWith('dyna~') ?? false,
          body: (job: Job) => {
            const { project_ref, project_path } = job;
            if (project_ref && project_path) {
              const el =
                AppRoutes.projects.detail.url(
                  'projects/'.concat(project_path).concat('/ref/').concat(project_ref).concat('/project')
                ) + Utils.buildNewQueryParamsUrl({ tabs: '1', process_global: processId! });
              return (
                <NavLink className="text-color hover:text-primary" to={el}>
                  {job.project_ref}
                </NavLink>
              );
            }
          },
          filterMatchModeOptions: [
            { label: 'Contains', value: FilterMatchMode.CONTAINS },
            { label: 'Start With', value: FilterMatchMode.STARTS_WITH }
          ]
        },
        {
          field: 'actions',
          header: 'Actions',
          body: (item: Job) => (
            <ActionBodyRender
              refreshDataCallback={() => refetch()}
              job={item}
              jobReference={prefilters.reference as string}
              key={item.jobID}
            />
          )
        }
      ];
    }
    return defaultJobsDatableColumns;
  }, [prefilters, refetch, processId, t]);

  return (
    <div>
      <section className={'flex flex-border-2'}>
        <div className={'flex flex-column align-items-start w-full'}>
          <JobsDataTable
            onRefresh={{
              command: refetch
            }}
            dataTableClassName="text-sm"
            columns={columns}
            jobs={data?.jobs ?? []}
            getCapabilities={getCapabilities}
            loading={isPending}
            defaultFilters={prefilters}
            onClearFilters={{
              command: (filters) => updateFilters(filters)
            }}
            handlePropertiesFilters={(filters) => updateFilters(filters)}
            handleGlobalFilters={(filters) => updateFilters(filters, true)}
          />
        </div>
      </section>
    </div>
  );
};
